import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { iframeMaxWidthLeft as maxWidthLeft } from 'javascript/Constants'
import { getClosedIconMessagesToday, iconMessageClosedToday } from 'Utilities/ClosedIconMessages'
import i18n from 'javascript/i18n'
import { useAppStore } from 'Store/app'
import { useSettingsStore } from 'Store/settings'
import { matchUrlConditions, isMobile } from 'Utilities'
import userSync from 'javascript/helpers/userSync'
import { Cdp } from 'src/apis'
import _uniqBy from 'lodash/uniqBy'

const showDeviceTypes = {
  any: 'any',
  desktop: 'desktop',
  mobile: 'mobile'
}

export const useIconMessageStore = defineStore('IconMessage', () => {
  const width = ref(0)
  const height = ref(0)
  const iframeMaxWidthLeft = ref(maxWidthLeft)
  const iframeMaxWidth = ref(0)
  const iframeMaxHeight = ref(0)
  const closedIconMessages = ref(getClosedIconMessagesToday())
  const position = ref('left')
  const sendImageMessage = ref(null)
  const messages = ref({
    en: [],
    ja: [],
    zh_Hans: [],
    zh_Hant: [],
    ko: [],
    id: [],
    ar: [],
    th: []
  })
  const triggerFlow = ref(null)
  const triggerFlowId = ref(null)

  // getters
  const getIconMessages = computed(() => {
    const locale = i18n.locale
    const appStore = useAppStore()

    return messages.value[locale].filter((text) => {
      if (!text.showDevice) {
        return true
      }
      if (appStore.isFullScreen) {
        return text.showDevice === showDeviceTypes.any || text.showDevice === showDeviceTypes.mobile
      } else {
        return (
          text.showDevice === showDeviceTypes.any || text.showDevice === showDeviceTypes.desktop
        )
      }
    })
  })

  const displayedIconMessages = computed(() => {
    const appStore = useAppStore()
    const settingsStore = useSettingsStore()

    if (
      appStore.isMinimized ||
      !appStore.session ||
      !appStore.iframesHaveLoadedCSS.includes('iconmessagewindow-app')
    ) {
      return []
    }

    const iconMessages = getIconMessages.value.filter((iconMessage) => {
      const isUrlMatched = matchUrlConditions(iconMessage.url_patterns, appStore.windowLocation)
      const isPlatformMatched =
        appStore.isOnBookingWidget || settingsStore.isOnStandaloneBookingWidget
          ? iconMessage.show_on_platforms.includes('booking_widget') && !isMobile()
          : iconMessage.show_on_platforms.includes('chatbot')
      const isClosed = closedIconMessages.value.some((icon) => icon.id === iconMessage.id)

      return isUrlMatched && isPlatformMatched && !isClosed
    })

    return _uniqBy(iconMessages, 'id')
  })

  const getIframeHeight = computed(() => `${height.value}px`)

  const getIframeWidth = computed(() => `${Math.min(iframeMaxWidth.value, width.value)}px`)

  function resetIconMessageIframeSize() {
    // Brr widget will bouncing if icon message has delayed.
    // So iframeMaxHeight sometimes is a really big number.
    // I want to skip it for height.value.
    if (iframeMaxHeight.value - height.value > 700) return
    width.value = iframeMaxWidth.value
    height.value = iframeMaxHeight.value
  }

  function updateIframeHeight(payload) {
    height.value = payload
  }

  function updateIframeWidth(payload) {
    width.value = payload
  }

  function updateIframeMaxWidthLeft(payload) {
    iframeMaxWidthLeft.value = payload
  }

  function updateIframeMaxWidth(payload) {
    iframeMaxWidth.value = payload
  }

  function updateIframeMaxHeight(payload) {
    iframeMaxHeight.value = payload
  }

  function updatePosition(payload) {
    position.value = payload
  }

  function setClosedIconMessagse(payload) {
    closedIconMessages.value = payload
  }

  function clearIconMessages() {
    messages.value = {
      en: [],
      ja: [],
      zh_Hans: [],
      zh_Hant: [],
      ko: [],
      id: [],
      ar: [],
      th: []
    }
  }

  function clearCDPIconMessages() {
    const newMessages = {}
    Object.entries(messages.value).forEach(([language, iconMessages]) => {
      newMessages[language] = iconMessages.filter((iconMessage) => !iconMessage.segment_ids)
    })
    messages.value = newMessages
  }

  function appendIconMessage(payload) {
    messages.value[payload.language].push(payload.message)
  }

  function setIconMessageAction(payload) {
    triggerFlow.value = payload.triggerFlow
    triggerFlowId.value = payload.triggerFlowId
  }

  function resetIconMessageAction() {
    triggerFlow.value = null
    triggerFlowId.value = null
  }

  function setSendImageMessage(payload) {
    sendImageMessage.value = payload
  }

  function resetSendImageMessage(payload) {
    sendImageMessage.value = null
  }

  function resetClosedIconMessage() {
    setClosedIconMessagse(getClosedIconMessagesToday())
  }

  function closeIconMessage(iconMessage) {
    userSync.setState({ closedIconMessage: iconMessage })
    setClosedIconMessagse([
      ...closedIconMessages.value,
      { id: iconMessage.id, last_closed: new Date() }
    ])
  }

  function appendIconMessages(payload) {
    for (const iconMessage of payload.messages) {
      if (!iconMessage) continue // CON-18794 - Allow for missing icon messages.
      if (iconMessageClosedToday(iconMessage)) continue

      const message = {
        ...iconMessage,
        backgroundColor: iconMessage.background_color,
        hideDelay: iconMessage.hide_delay,
        showDevice: iconMessage.show_device,
        textColor: iconMessage.text_color
      }

      if (iconMessage.type === 'icon_image') {
        message['buttonText'] = iconMessage.button_text
        message['triggerFlow'] = iconMessage.trigger_flow
      }

      const newPayload = { language: payload.language, message }

      if (iconMessage.show_delay > 0) {
        setTimeout(() => {
          appendIconMessage(newPayload)
        }, iconMessage.show_delay * 1000)
      } else {
        appendIconMessage(newPayload)
      }
    }
  }

  function fetchCDPIconMessages() {
    const settingsStore = useSettingsStore()

    clearCDPIconMessages()
    Cdp.getSegmentedIconMessages(settingsStore.getHotelId).then((data) => {
      Object.entries(data).forEach(([language, iconData]) => {
        appendIconMessages({ language, messages: iconData.icon.icon_message })
      })
    })
  }

  return {
    width,
    height,
    iframeMaxWidthLeft,
    iframeMaxWidth,
    iframeMaxHeight,
    closedIconMessages,
    position,
    sendImageMessage,
    messages,
    triggerFlow,
    triggerFlowId,

    // getters
    getIconMessages,
    displayedIconMessages,
    getIframeHeight,
    getIframeWidth,

    // actions
    resetIconMessageIframeSize,
    updateIframeHeight,
    updateIframeWidth,
    updateIframeMaxWidthLeft,
    updateIframeMaxWidth,
    updateIframeMaxHeight,
    updatePosition,
    clearIconMessages,
    setIconMessageAction,
    resetIconMessageAction,
    setSendImageMessage,
    resetSendImageMessage,
    resetClosedIconMessage,
    closeIconMessage,
    appendIconMessages,
    fetchCDPIconMessages
  }
})
